"use client";

import { Fragment } from "react";
import { useAuth } from "@/hooks/useAuth";
import { XIcon } from "./icons/XIcon";
import CdnImage from "@/components/CdnImage";
import { Dialog, Transition } from "@headlessui/react";

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * LoginModal - Simplified with Privy
 *
 * Privy handles all login methods (email, wallet, social) through its own UI.
 * This modal just triggers the Privy login flow.
 */
export default function LoginModal({ isOpen, onClose }: LoginModalProps) {
  const { login } = useAuth();

  const handleLogin = () => {
    onClose(); // Close our modal
    login();   // Open Privy's login modal
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-[5px] bg-white dark:bg-[#0F0F12] border-2 border-gray-100 dark:border-[#CCFF00]/20 shadow-[0_0_50px_rgba(204,255,0,0.15)] transition-all">
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#CCFF00]/[0.03] to-[#00FF9D]/[0.03] pointer-events-none" />

                {/* Close button */}
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 z-10 p-2 rounded-[5px] hover:bg-gray-100 dark:hover:bg-[#CCFF00]/10 transition-colors group"
                >
                  <XIcon size={20} className="text-gray-500 dark:text-gray-400 group-hover:text-[#CCFF00] transition-colors" />
                </button>

                <div className="p-8 relative">
                  {/* Logo & Title */}
                  <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <CdnImage
                        src="/brand/frenflow_logo_light.png"
                        alt="FrenFlow"
                        width={40}
                        height={40}
                        className="w-10 h-10 dark:hidden"
                      />
                      <CdnImage
                        src="/brand/frenflow_logo.png"
                        alt="FrenFlow"
                        width={40}
                        height={40}
                        className="w-10 h-10 hidden dark:block"
                      />
                      <div className="text-3xl tracking-tight flex items-baseline">
                        <span className="text-[#111729] dark:text-white font-monument font-bold">FREN</span>
                        <span className="text-[#111729] dark:text-white font-satoshi font-light">FLOW</span>
                      </div>
                    </div>
                    <p className="text-gray-600 dark:text-[#A1A1AA] font-inter-tight">
                      Sign in to start trading
                    </p>
                  </div>

                  {/* Sign In Button */}
                  <button
                    onClick={handleLogin}
                    className="w-full py-4 px-4 bg-gradient-to-r from-[#CCFF00] to-[#00FF9D] text-[#0A0A0A] font-monument font-bold rounded-[5px] hover:scale-105 transition-all"
                  >
                    Sign In
                  </button>

                  <p className="text-xs text-center text-gray-500 dark:text-[#A1A1AA] mt-4 font-inter-tight">
                    Email, Google, Twitter, or Wallet
                  </p>
                </div>

                {/* Footer */}
                <div className="px-8 pb-6">
                  <p className="text-center text-xs text-gray-500 dark:text-[#A1A1AA] font-inter-tight">
                    By signing in, you agree to our Terms of Service and Privacy Policy
                  </p>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
